<script setup lang="ts">
import { X } from "lucide-vue-next";

const props = defineProps<{
  modelValue: boolean;
  images: string[];
  alt: string;
  title: string;
  price: string;
  description: string;
  category?: string;
  initialIndex?: number;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

type GalleryExpose = {
  previousPhoto: () => void;
  nextPhoto: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetView: () => void;
};

const PAGE_LOCK_CLASS = "service-details-modal-open";
const FULLSCREEN_CLASS = "service-details-fullscreen-open";
const PAGE_SCROLL_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
]);

const gallery = ref<GalleryExpose | null>(null);
const fullscreenOpen = ref(false);

let pageScrollLocked = false;
let restoringScrollPosition = false;
let lockedScrollX = 0;
let lockedScrollY = 0;
let previousHtmlScrollBehavior = "";
let previousHtmlOverflowAnchor = "";
let previouslyFocusedElement: HTMLElement | null = null;
let scrollToContactsAfterClose = false;

watch(
  () => props.modelValue,
  open => {
    if (!open) {
      fullscreenOpen.value = false;
      return;
    }

    fullscreenOpen.value = false;
    scrollToContactsAfterClose = false;
    lockPageScroll();
  },
  { flush: "sync" },
);

watch(fullscreenOpen, fullscreen => {
  if (!import.meta.client) return;

  document.documentElement.classList.toggle(FULLSCREEN_CLASS, fullscreen);
  document.body.classList.toggle(FULLSCREEN_CLASS, fullscreen);
});

function isModalScrollableTarget(target: EventTarget | null) {
  return (
    target instanceof Element &&
    Boolean(target.closest("[data-service-modal-scroll]"))
  );
}

function preventBackgroundScroll(event: Event) {
  if (!pageScrollLocked || isModalScrollableTarget(event.target)) return;

  event.preventDefault();
}

function keepPageScrollPosition() {
  if (
    !pageScrollLocked ||
    restoringScrollPosition ||
    (window.scrollX === lockedScrollX && window.scrollY === lockedScrollY)
  ) {
    return;
  }

  restoringScrollPosition = true;
  window.scrollTo(lockedScrollX, lockedScrollY);

  requestAnimationFrame(() => {
    restoringScrollPosition = false;
  });
}

function lockPageScroll() {
  if (!import.meta.client || pageScrollLocked) return;

  const html = document.documentElement;
  const body = document.body;

  lockedScrollX = window.scrollX;
  lockedScrollY = window.scrollY;
  previouslyFocusedElement = document.activeElement as HTMLElement | null;
  previousHtmlScrollBehavior = html.style.scrollBehavior;
  previousHtmlOverflowAnchor = html.style.overflowAnchor;

  pageScrollLocked = true;

  html.style.scrollBehavior = "auto";
  html.style.overflowAnchor = "none";
  html.classList.add(PAGE_LOCK_CLASS);
  body.classList.add(PAGE_LOCK_CLASS);

  window.addEventListener("keydown", handleKeydown);
  window.addEventListener("wheel", preventBackgroundScroll, {
    passive: false,
  });
  window.addEventListener("touchmove", preventBackgroundScroll, {
    passive: false,
  });
  window.addEventListener("scroll", keepPageScrollPosition, {
    passive: true,
  });

  requestAnimationFrame(() => {
    if (pageScrollLocked) {
      window.scrollTo(lockedScrollX, lockedScrollY);
    }
  });
}

function unlockPageScroll() {
  if (!import.meta.client || !pageScrollLocked) return;

  pageScrollLocked = false;

  window.removeEventListener("keydown", handleKeydown);
  window.removeEventListener("wheel", preventBackgroundScroll);
  window.removeEventListener("touchmove", preventBackgroundScroll);
  window.removeEventListener("scroll", keepPageScrollPosition);

  const html = document.documentElement;
  const body = document.body;

  html.classList.remove(PAGE_LOCK_CLASS, FULLSCREEN_CLASS);
  body.classList.remove(PAGE_LOCK_CLASS, FULLSCREEN_CLASS);

  window.scrollTo(lockedScrollX, lockedScrollY);

  html.style.scrollBehavior = previousHtmlScrollBehavior;
  html.style.overflowAnchor = previousHtmlOverflowAnchor;
}

function handleKeydown(event: KeyboardEvent) {
  if (!props.modelValue) return;

  if (event.key === "Escape") {
    event.preventDefault();

    if (fullscreenOpen.value) {
      fullscreenOpen.value = false;
    } else {
      close();
    }

    return;
  }

  if (event.key === "ArrowLeft") {
    event.preventDefault();
    gallery.value?.previousPhoto();
    return;
  }

  if (event.key === "ArrowRight") {
    event.preventDefault();
    gallery.value?.nextPhoto();
    return;
  }

  if (fullscreenOpen.value && (event.key === "+" || event.key === "=")) {
    event.preventDefault();
    gallery.value?.zoomIn();
    return;
  }

  if (fullscreenOpen.value && event.key === "-") {
    event.preventDefault();
    gallery.value?.zoomOut();
    return;
  }

  if (fullscreenOpen.value && event.key === "0") {
    event.preventDefault();
    gallery.value?.resetView();
    return;
  }

  if (PAGE_SCROLL_KEYS.has(event.key)) {
    const target = event.target as HTMLElement | null;

    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target?.isContentEditable
    ) {
      return;
    }

    event.preventDefault();
  }
}

function close() {
  emit("update:modelValue", false);
}

function closeAndGoToContacts() {
  scrollToContactsAfterClose = true;
  close();
}

function afterModalLeave() {
  if (props.modelValue) return;

  unlockPageScroll();

  previouslyFocusedElement?.focus?.({ preventScroll: true });
  previouslyFocusedElement = null;

  if (!scrollToContactsAfterClose) return;

  scrollToContactsAfterClose = false;

  requestAnimationFrame(() => {
    document.getElementById("contacts")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  });
}

onBeforeUnmount(() => {
  if (import.meta.client) {
    document.documentElement.classList.remove(FULLSCREEN_CLASS);
    document.body.classList.remove(FULLSCREEN_CLASS);
  }

  unlockPageScroll();
});
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="service-modal" @after-leave="afterModalLeave">
        <div
          v-if="modelValue"
          class="fixed inset-0 z-[5000] flex items-center justify-center bg-black/55 p-0 backdrop-blur-[1px] lg:p-5"
          role="presentation"
          @click.self="close"
        >
          <section
            role="dialog"
            aria-modal="true"
            :aria-label="$t('worker_profile.service_details')"
            class="relative flex h-[100dvh] w-full min-w-0 flex-col overflow-hidden bg-white shadow-2xl lg:h-[700px] lg:max-h-[calc(100dvh-40px)] lg:max-w-[1160px] lg:rounded-2xl"
            @click.stop
          >
            <button
              type="button"
              class="absolute right-3 top-3 z-40 flex size-10 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              :aria-label="$t('worker_profile.close_service_details')"
              @click="close"
            >
              <X class="size-5" />
            </button>

            <div
              class="grid min-h-0 flex-1 grid-rows-[minmax(260px,44dvh)_minmax(0,1fr)] lg:grid-cols-[minmax(0,1.6fr)_minmax(340px,0.9fr)] lg:grid-rows-1 xl:grid-cols-[minmax(0,1.7fr)_minmax(350px,0.85fr)]"
            >
              <WorkerPublicServiceDetailsGallery
                ref="gallery"
                v-model:fullscreen="fullscreenOpen"
                :images="images"
                :alt="alt"
                :title="title"
                :price="price"
                :initial-index="initialIndex"
              />

              <aside class="flex min-h-0 flex-col bg-white">
                <div
                  data-service-modal-scroll
                  class="service-details-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 pt-6 sm:px-7 lg:px-8 lg:pb-7 lg:pt-8"
                >
                  <div class="flex flex-wrap items-center gap-2">
                    <p
                      class="text-xs font-bold uppercase tracking-[0.12em] text-primary"
                    >
                      {{ $t("worker_profile.service_details") }}
                    </p>

                    <span
                      v-if="category"
                      class="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary"
                    >
                      {{ category }}
                    </span>
                  </div>

                  <h2
                    class="mt-2 break-words text-2xl font-bold leading-tight text-app-text xl:text-3xl"
                  >
                    {{ title }}
                  </h2>

                  <div class="mt-5 border-y border-app-border py-4">
                    <p class="text-sm font-medium text-app-muted">
                      {{ $t("worker_profile.service_price") }}
                    </p>

                    <strong
                      class="mt-1 block break-words text-2xl font-bold leading-tight text-primary"
                    >
                      {{ price }}
                    </strong>
                  </div>

                  <div class="mt-5">
                    <h3 class="text-base font-bold text-app-text">
                      {{ $t("worker_profile.service_description") }}
                    </h3>

                    <p
                      class="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-app-muted sm:text-base sm:leading-7"
                    >
                      {{ description }}
                    </p>
                  </div>
                </div>

                <div
                  class="shrink-0 border-t border-app-border bg-white p-5 sm:px-7 lg:px-8"
                >
                  <button
                    type="button"
                    class="inline-flex min-h-12 w-full cursor-pointer items-center justify-center rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white transition hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                    @click="closeAndGoToContacts"
                  >
                    {{ $t("worker_profile.contact_professional") }}
                  </button>
                </div>
              </aside>
            </div>
          </section>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
:global(html.service-details-modal-open),
:global(body.service-details-modal-open) {
  scrollbar-color: transparent rgb(70 70 70);
}

:global(html.service-details-modal-open::-webkit-scrollbar-thumb),
:global(body.service-details-modal-open::-webkit-scrollbar-thumb) {
  background: transparent !important;
}

:global(html.service-details-modal-open::-webkit-scrollbar-track),
:global(body.service-details-modal-open::-webkit-scrollbar-track),
:global(html.service-details-modal-open::-webkit-scrollbar-corner),
:global(body.service-details-modal-open::-webkit-scrollbar-corner) {
  background: rgb(70 70 70) !important;
}

:global(html.service-details-fullscreen-open),
:global(body.service-details-fullscreen-open) {
  scrollbar-color: transparent #000;
}

:global(html.service-details-fullscreen-open::-webkit-scrollbar-track),
:global(body.service-details-fullscreen-open::-webkit-scrollbar-track),
:global(html.service-details-fullscreen-open::-webkit-scrollbar-corner),
:global(body.service-details-fullscreen-open::-webkit-scrollbar-corner) {
  background: #000 !important;
}

.service-modal-enter-active,
.service-modal-leave-active {
  transition: opacity 180ms ease;
}

.service-modal-enter-active section,
.service-modal-leave-active section {
  transition:
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.service-modal-enter-from,
.service-modal-leave-to {
  opacity: 0;
}

.service-modal-enter-from section,
.service-modal-leave-to section {
  opacity: 0;
  transform: translateY(12px) scale(0.985);
}

.service-details-scroll {
  scrollbar-color: rgb(148 163 184 / 0.72) transparent;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}

.service-details-scroll::-webkit-scrollbar {
  width: 9px;
}

.service-details-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.service-details-scroll::-webkit-scrollbar-thumb {
  min-height: 44px;
  border: 2px solid transparent;
  border-radius: 999px;
  background: rgb(148 163 184 / 0.72);
  background-clip: content-box;
}

.service-details-scroll::-webkit-scrollbar-thumb:hover {
  background: rgb(100 116 139 / 0.88);
  background-clip: content-box;
}

@media (prefers-reduced-motion: reduce) {
  .service-modal-enter-active,
  .service-modal-leave-active,
  .service-modal-enter-active section,
  .service-modal-leave-active section {
    transition-duration: 1ms;
  }
}
</style>
